import { useApp } from '../context/AppContext';
import { getStats, getMemberById, getFamilyForMember } from '../utils/calculator';
import { formatCurrency, formatCurrencyShort, formatDateShort, getCategory, getAllCategoryKeys, getFamilyColor } from '../utils/helpers';

export default function Dashboard() {
  const { expenses, payments, families } = useApp();
  if (!Object.keys(families).length) return <div className="page-loading">Loading...</div>;

  const stats = getStats(expenses, payments, families);
  const { settlement } = stats;

  const payerName   = settlement.payerFamily   ? families[settlement.payerFamily]?.name   : '';
  const receiverName = settlement.receiverFamily ? families[settlement.receiverFamily]?.name : '';

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Trip expense overview and analytics</p>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="stats-grid">
        <div className="stat-card glass-card">
          <div className="stat-card-icon" style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8' }}>💰</div>
          <div className="stat-card-info">
            <span className="stat-card-label">Total Trip Cost</span>
            <span className="stat-card-value">{formatCurrency(stats.grandTotal)}</span>
          </div>
        </div>
        <div className="stat-card glass-card">
          <div className="stat-card-icon" style={{ background: 'rgba(16,185,129,0.15)', color: '#34d399' }}>📊</div>
          <div className="stat-card-info">
            <span className="stat-card-label">Total Expenses</span>
            <span className="stat-card-value">{stats.totalExpenses}</span>
          </div>
        </div>
        <div className="stat-card glass-card">
          <div className="stat-card-icon" style={{ background: 'rgba(245,158,11,0.15)', color: '#fbbf24' }}>📅</div>
          <div className="stat-card-info">
            <span className="stat-card-label">Daily Average</span>
            <span className="stat-card-value">{formatCurrency(stats.dailyAverage)}</span>
          </div>
        </div>
        <div className="stat-card glass-card">
          <div className="stat-card-icon" style={{ background: 'rgba(236,72,153,0.15)', color: '#f472b6' }}>🏷️</div>
          <div className="stat-card-info">
            <span className="stat-card-label">Top Category</span>
            <span className="stat-card-value">
              {getCategory(stats.highestCategory).icon} {getCategory(stats.highestCategory).label}
            </span>
          </div>
        </div>
      </div>

      {/* Settlement Mini */}
      {stats.totalExpenses > 0 && (
        <div className="settlement-mini glass-card">
          {settlement.amount === 0 ? (
            <span className="settlement-mini-text">⚖️ All Balanced — No money owed</span>
          ) : (
            <span className="settlement-mini-text">
              {payerName} owes {receiverName}:{' '}
              <strong>{formatCurrency(settlement.remaining)}</strong>
              {settlement.isSettled && <span className="text-green"> ✓ Settled</span>}
            </span>
          )}
        </div>
      )}

      <div className="report-grid">
        {/* Category Breakdown */}
        <div className="glass-card report-section">
          <h3 className="section-title">Spending by Category</h3>
          {stats.totalExpenses === 0 ? (
            <p className="text-muted">No expenses recorded yet</p>
          ) : (
            <div className="category-bars">
              {getAllCategoryKeys().map(catKey => {
                const cat = getCategory(catKey);
                const data = stats.byCategory[catKey];
                const pct = stats.grandTotal > 0 ? (data.total / stats.grandTotal * 100) : 0;
                if (data.total === 0) return null;
                return (
                  <div className="cat-bar-row" key={catKey}>
                    <div className="cat-bar-label">
                      <span>{cat.icon} {cat.label}</span>
                      <span className="cat-bar-amount">{formatCurrency(data.total)} <span className="text-muted">({pct.toFixed(0)}%)</span></span>
                    </div>
                    <div className="cat-bar-track">
                      <div className="cat-bar-fill" style={{ width: `${pct}%`, background: cat.color }} />
                    </div>
                    <div className="cat-bar-split">
                      <span style={{ color: '#34d399' }}>F1: {formatCurrencyShort(data.family1Share)}</span>
                      <span style={{ color: '#60a5fa' }}>F2: {formatCurrencyShort(data.family2Share)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Family Comparison */}
        <div className="glass-card report-section">
          <h3 className="section-title">Family Comparison</h3>
          {stats.totalExpenses === 0 ? (
            <p className="text-muted">No expenses recorded yet</p>
          ) : (
            <div className="family-comparison">
              {['family1', 'family2'].map((fid, i) => {
                const fam = families[fid];
                const color = getFamilyColor(fid);
                const data = settlement.totals[fid];
                const paidPct = stats.grandTotal > 0 ? (data.paid / stats.grandTotal * 100) : 0;
                const shouldPct = stats.grandTotal > 0 ? (data.shouldPay / stats.grandTotal * 100) : 0;
                return (
                  <div key={fid}>
                    {i > 0 && <div className="compare-divider" />}
                    <div className="family-compare-card">
                      <h4 style={{ color: color.text }}>{fam.name}</h4>
                      <div className="compare-row">
                        <span>Paid</span>
                        <div className="compare-bar-track"><div className="compare-bar-fill" style={{ width: `${paidPct}%`, background: color.border }} /></div>
                        <span>{formatCurrencyShort(data.paid)}</span>
                      </div>
                      <div className="compare-row">
                        <span>Owes</span>
                        <div className="compare-bar-track"><div className="compare-bar-fill" style={{ width: `${shouldPct}%`, background: `${color.border}50` }} /></div>
                        <span>{formatCurrencyShort(data.shouldPay)}</span>
                      </div>
                      <div className={`compare-diff ${data.difference >= 0 ? 'text-green' : 'text-red'}`}>
                        {data.difference >= 0 ? 'Overpaid' : 'Underpaid'}: {formatCurrency(Math.abs(data.difference))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Recent Expenses */}
      {stats.totalExpenses > 0 && (
        <div className="glass-card report-section" style={{ marginTop: '1.5rem' }}>
          <h3 className="section-title">Recent Expenses</h3>
          <div className="recent-list">
            {[...expenses].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5).map(exp => {
              const cat = getCategory(exp.category);
              const member = getMemberById(exp.paidBy, families);
              return (
                <div className="recent-row" key={exp.id}>
                  <span className="recent-icon" style={{ color: cat.color }}>{cat.icon}</span>
                  <span className="recent-desc">{exp.description}</span>
                  <span className="recent-payer text-muted">{member?.name || '?'}</span>
                  <span className="recent-date text-muted">{formatDateShort(exp.date)}</span>
                  <span className="recent-amount">{formatCurrency(exp.amount)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
